/**
 * The requirements hierarchy, banded by level with L0 at the top.
 *
 * The one view here that WRITES. Dragging a box's handle onto another sets the
 * dragged requirement's `parent`, which is the fastest way to fix a flowdown
 * that came out of a CSV import with the tracing wrong — the alternative is
 * opening each requirement and picking a parent from a list of two hundred.
 *
 * The cycle is refused before the request is sent, by name. The back end would
 * refuse it too, but a round trip later and in terms of rows rather than in
 * terms of the diagram somebody is looking at.
 *
 * AT PROGRAM SIZE (issue #93) the whole model in one picture is unreadable, so
 * the diagram draws a subset, chosen two ways that compose:
 *
 * - a FOCUS: one requirement, or every requirement of a segment or subsystem,
 *   with everything each derives from up to L0 and everything derived from it
 *   (`focus.lineage`). "Whole model" is always one click away.
 * - a FOLD: by level ("show down to L1") and box by box (`focus.fold`). Above
 *   FOLD_BY_DEFAULT_ABOVE requirements the whole model OPENS folded to its
 *   second level, each box saying how much is under it; at or below that, and
 *   for any focus, nothing is folded until somebody folds it.
 *
 * The drag still checks cycles against EVERY requirement, not the drawn subset:
 * a cycle through a box that is folded away is still a cycle.
 */

import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { ApiError, api } from '../../api'
import { findKind } from '../../schema'
import type { FlowdownRow } from '../../schema'
import { useCanWrite, useSchema } from '../../state/AppData'
import { useToasts } from '../../state/Toasts'
import EmptyState from '../common/EmptyState'
import DiagramCanvas from './DiagramCanvas'
import FoldBar from './FoldBar'
import Legend from './Legend'
import { FOLD_BY_DEFAULT_ABOVE, UNFOLDED, defaultCutoff, fold, lineage, toggle } from './focus'
import type { Folding } from './focus'
import {
  facetValues,
  layout,
  requirementBandLabels,
  requirementNodes,
  requirementSeeds,
} from './layout'
import type { DiagramNode, RequirementFacet } from './layout'
import { checkReparent } from './reparent'
import { useDiagramRows } from './useDiagramRows'

/** What the diagram is focused on. */
type Focus =
  | { kind: 'all' }
  | { kind: 'requirement'; key: string }
  | { kind: RequirementFacet; value: string }

const WHOLE_MODEL: Focus = { kind: 'all' }

/** One string per focus, so the canvas re-fits when the focus changes, and only then. */
function focusKey(focus: Focus): string {
  if (focus.kind === 'all') return 'all'
  return focus.kind === 'requirement' ? `requirement:${focus.key}` : `${focus.kind}:${focus.value}`
}

export default function RequirementsHierarchy() {
  const { rows, error, reload } = useDiagramRows<FlowdownRow>(api.flowdown)
  const schema = useSchema()
  const { allowed, reason } = useCanWrite()
  const toasts = useToasts()
  const navigate = useNavigate()

  const plural = findKind(schema, 'requirement')?.plural ?? 'requirements'

  const nodes = useMemo(() => (rows ? requirementNodes(rows) : []), [rows])
  const labels = useMemo(() => requirementBandLabels(nodes), [nodes])
  const orphans = nodes.filter((node) => node.isOrphan).length

  const [focus, setFocus] = useState<Focus>(WHOLE_MODEL)
  // Null until somebody folds something. The default follows the model's size,
  // which is not known until the rows arrive - and it survives a reload after
  // a drag, so adding a source does not unfold the diagram under the pointer.
  const [chosenFolding, setFolding] = useState<Folding | null>(null)

  const scoped = useMemo(() => {
    if (!rows || focus.kind === 'all') return nodes
    const seeds =
      focus.kind === 'requirement' ? [focus.key] : requirementSeeds(rows, focus.kind, focus.value)
    return lineage(nodes, seeds)
  }, [rows, nodes, focus])

  const folding = useMemo<Folding>(
    () =>
      chosenFolding ??
      (focus.kind === 'all' ? { cutoff: defaultCutoff(nodes), open: new Map() } : UNFOLDED),
    [chosenFolding, focus.kind, nodes],
  )
  const folded = useMemo(() => fold(scoped, folding), [scoped, folding])
  const diagram = useMemo(() => layout(folded.nodes, labels), [folded, labels])
  const bands = useMemo(
    () =>
      [...new Set(scoped.map((node) => node.band))]
        .sort((a, b) => a - b)
        .map((band) => ({ band, label: labels[band] ?? String(band) })),
    [scoped, labels],
  )
  const segments = useMemo(() => (rows ? facetValues(rows, 'segment') : []), [rows])
  const subsystems = useMemo(() => (rows ? facetValues(rows, 'subsystem') : []), [rows])

  const refocus = (next: Focus) => {
    setFocus(next)
    setFolding(null)
  }

  const drop = (childKey: string, parentKey: string) => {
    const verdict = checkReparent(nodes, childKey, parentKey)
    if (!verdict.allowed) {
      // A warning, not an error: nothing went wrong, the drag was refused. The
      // reason names both elements, so it is worth eight seconds on screen.
      toasts.notify(verdict.reason, 'warning')
      return
    }
    const child = nodes.find((node) => node.key === childKey)
    const parent = nodes.find((node) => node.key === parentKey)
    // ADDS A SOURCE, and does not replace one. Since issue #32 a requirement
    // may derive from several, so dragging it onto another states one more
    // rather than moving the only one it had — which is why this is a POST to
    // its own route and not a PATCH of a `parent` field.
    api
      .addSource(childKey, parentKey)
      .then(() => {
        toasts.succeeded(
          `${child?.label ?? childKey} now derives from ${parent?.label ?? parentKey}.`,
        )
        reload()
      })
      .catch((failure: ApiError) => toasts.failed(failure.message))
  }

  if (error) return <Alert severity="error">{error.message}</Alert>
  if (!rows) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }
  if (nodes.length === 0) {
    return (
      <EmptyState
        title="There are no requirements to draw"
        description="This diagram is generated from GET /api/flowdown. Import a model, or create a requirement, and it appears here."
        actions={[{ label: 'Go to requirements', onClick: () => navigate(`/${plural}`) }]}
      />
    )
  }

  const foldedByDefault = focus.kind === 'all' && chosenFolding === null && folding.cutoff !== null

  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        Every requirement, in the band of its stated level, tracing up to each source it derives
        from. Generated from <code>GET /api/flowdown</code>; the band is the requirement’s own{' '}
        <code>level</code>, not its depth in the tree, so a requirement that skips a level sits in
        its own row.
        {orphans > 0 &&
          ` ${orphans} requirement${orphans === 1 ? '' : 's'} below L0 name no source; ` +
            'they are drawn on their own and marked, because that is the finding.'}
      </Typography>

      <FocusPicker
        nodes={nodes}
        focus={focus}
        segments={segments}
        subsystems={subsystems}
        onFocus={refocus}
      />

      {(focus.kind !== 'all' || foldedByDefault) && (
        <Typography variant="body2" color="text.secondary">
          {focus.kind !== 'all'
            ? describeFocus(focus, nodes, scoped.length)
            : `This program has ${nodes.length} requirements, more than ${FOLD_BY_DEFAULT_ABOVE}, ` +
              `so the diagram opens folded down to ${labels[folding.cutoff as number]}, each box ` +
              'saying how many are under it. Open a box with its +N, choose a level, or focus on ' +
              'one requirement, segment or subsystem.'}
        </Typography>
      )}

      <FoldBar
        bands={bands}
        folding={folding}
        onChange={setFolding}
        shown={folded.nodes.length}
        total={scoped.length}
        noun="requirements"
      />

      <Legend
        items={[
          { tone: 'primary', label: 'Requirement' },
          { tone: 'warning', label: 'TBD / TBR / TBC' },
          { tone: 'error', label: 'Orphan — no source, below L0', dashed: true },
        ]}
      />

      <DiagramCanvas
        key={focusKey(focus)}
        diagram={diagram}
        emptyLabel="Nothing to draw."
        folds={folded.folds}
        onToggle={(key, state) => setFolding(toggle(folding, key, state))}
        onOpen={(node) => {
          if (node.id) navigate(`/${plural}/${encodeURIComponent(node.id)}`)
        }}
        reparent={{
          allowed,
          reason,
          verb: 'derives from',
          onDrop: drop,
        }}
      />
    </Stack>
  )
}

/**
 * Choose what to draw: one requirement, a segment, a subsystem, or everything.
 *
 * Three controls rather than one, because they answer three different
 * questions. Choosing in one clears the others, since a focus is one thing.
 */
function FocusPicker({
  nodes,
  focus,
  segments,
  subsystems,
  onFocus,
}: {
  nodes: DiagramNode[]
  focus: Focus
  segments: string[]
  subsystems: string[]
  onFocus: (next: Focus) => void
}) {
  const chosen =
    focus.kind === 'requirement' ? (nodes.find((node) => node.key === focus.key) ?? null) : null
  return (
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={{ alignItems: { md: 'center' } }}>
      <Autocomplete
        size="small"
        sx={{ minWidth: 300, flexGrow: 1, maxWidth: 520 }}
        options={nodes}
        value={chosen}
        getOptionLabel={(node) => (node.detail ? `${node.label} — ${node.detail}` : node.label)}
        isOptionEqualToValue={(option, value) => option.key === value.key}
        // Its own words for the pop-up button. The default is "Open", which on
        // this screen is already the name of every model view's button.
        openText="List requirements"
        closeText="Close the list"
        onChange={(_event, node) =>
          onFocus(node ? { kind: 'requirement', key: node.key } : WHOLE_MODEL)
        }
        renderInput={(params) => <TextField {...params} label="Focus on a requirement" />}
      />
      <FacetSelect
        label="Segment"
        values={segments}
        value={focus.kind === 'segment' ? focus.value : ''}
        onChange={(value) => onFocus(value ? { kind: 'segment', value } : WHOLE_MODEL)}
      />
      <FacetSelect
        label="Subsystem"
        values={subsystems}
        value={focus.kind === 'subsystem' ? focus.value : ''}
        onChange={(value) => onFocus(value ? { kind: 'subsystem', value } : WHOLE_MODEL)}
      />
      <Button size="small" disabled={focus.kind === 'all'} onClick={() => onFocus(WHOLE_MODEL)}>
        Whole model
      </Button>
    </Stack>
  )
}

/** A segment or subsystem drop-down; the blank entry means "not focused on one". */
function FacetSelect({
  label,
  values,
  value,
  onChange,
}: {
  label: string
  values: string[]
  value: string
  onChange: (value: string) => void
}) {
  return (
    <TextField
      select
      size="small"
      label={label}
      value={value}
      disabled={values.length === 0}
      onChange={(event) => onChange(event.target.value)}
      sx={{ minWidth: 160 }}
    >
      <MenuItem value="">
        <em>Any</em>
      </MenuItem>
      {values.map((entry) => (
        <MenuItem key={entry} value={entry}>
          {entry}
        </MenuItem>
      ))}
    </TextField>
  )
}

/** The sentence saying what a focused subset is. */
function describeFocus(focus: Focus, nodes: DiagramNode[], count: number): string {
  const plural = `${count} requirement${count === 1 ? '' : 's'}`
  if (focus.kind === 'requirement') {
    const label = nodes.find((node) => node.key === focus.key)?.label ?? focus.key
    return (
      `Focused on ${label}: everything it derives from, up to L0, and everything that derives ` +
      `from it — ${plural}. Its siblings are not drawn.`
    )
  }
  if (focus.kind === 'all') return ''
  return (
    `Focused on the ${focus.value} ${focus.kind}: every requirement stating it, with what each ` +
    `derives from and what derives from it — ${plural}.`
  )
}
